import classNames from 'classnames';
import { useCallback, useRef, useState } from 'react';
import { MaterialIcon } from 'shared/ui/MaterialIcon';
import { Chip } from 'shared/ui/Chip';
import { getDataAutoTestAttributes } from 'shared/lib/data-auto-test';
import type { Option } from 'shared/ui/MultiSelect/interfaces';
import { getOptionKey } from 'shared/ui/MultiSelect/Multiselect/utils';

import style from './ChipButton.module.scss';
import { HidableOption } from './HidableOption';

export interface ChipButtonProps {
  title: string;
  selectedOptions: Option[];
  setSelected: (options: Option[]) => void;
  onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  gutterWidth: number;
  isOpen: boolean;
  onFocus?: (e: React.FocusEvent<HTMLButtonElement>) => void;
  leftIcon?: string;
  size?: 'small' | 'big';
  disabled?: boolean;
  error?: string;
  isWhite?: boolean;
  isHighlighted?: boolean;
}

export function ChipButton({
  title,
  onFocus,
  leftIcon,
  selectedOptions,
  size,
  setSelected,
  gutterWidth,
  onClick,
  error,
  disabled,
  isOpen,
  isWhite,
  isHighlighted,
}: ChipButtonProps) {
  const parentRef = useRef<HTMLButtonElement | null>(null);
  const [hiddenOptions, setHiddenOptions] = useState<Set<string>>(new Set());

  const hasSelectedValues = selectedOptions.length > 0;

  const isSizeSmall = size === 'small';
  const shouldShowLabel = !(isSizeSmall && hasSelectedValues);

  const valuesBig = hasSelectedValues && !isSizeSmall;
  const valuesSmall = hasSelectedValues && isSizeSmall;

  const isHiddenCountShown = (idx: number) =>
    hiddenOptions.size > 0 && selectedOptions.length - 1 - hiddenOptions.size === idx;

  const handleHide = useCallback((id: string, hidden: boolean) => {
    setHiddenOptions((prev) => {
      const newSet = new Set(prev);

      if (hidden) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }

      return newSet;
    });
  }, []);

  const handleDelete = useCallback(
    (id?: string) => {
      setSelected(selectedOptions.filter((item) => getOptionKey(item.id, item.text) !== id));
    },

    [selectedOptions]
  );

  const handleClear = useCallback(
    (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
      e.stopPropagation();
      setSelected([]);
    },
    [setSelected]
  );

  return (
    <div
      className={classNames(
        'relative',
        hasSelectedValues && style.hasSelectedValues,
        leftIcon && style.hasLeftIcon,
        error && style.error,
        isSizeSmall && style.small,
        !isSizeSmall && style.big
      )}
    >
      <button
        type="button"
        ref={parentRef}
        className={classNames(
          style.multiselectBtn,
          { '!bg-white': isWhite },
          { '!border-deep-blue-500': isHighlighted },
          isOpen && style.open
        )}
        onFocus={onFocus}
        onClick={onClick}
        disabled={disabled}
      >
        {leftIcon && (
          <span className="mr-2 min-w-[20px]">
            <MaterialIcon className={style.leftIcon}>{leftIcon}</MaterialIcon>
          </span>
        )}
        <span className={classNames(style.chipBlock, valuesBig && 'pt-6', valuesSmall && 'pt-2')}>
          {shouldShowLabel && (
            <span
              className={classNames(style.label, {
                '!text-deep-blue-500': isHighlighted,
              })}
            >
              {title}
            </span>
          )}
          {hasSelectedValues && (
            <span className={style.selectedChipBlock}>
              {selectedOptions.map(({ id, text }, idx) => {
                const key = getOptionKey(id, text);

                return (
                  <HidableOption
                    key={key}
                    id={key}
                    parentRef={parentRef}
                    selectedOptionsLength={selectedOptions.length}
                    onHide={handleHide}
                    gutterWidth={gutterWidth}
                    isFirst={idx === 0}
                  >
                    <Chip
                      label={text}
                      id={key}
                      onClick={handleDelete}
                      actionIcon="close"
                      className="relative"
                    >
                      {isHiddenCountShown(idx) && (
                        <div className={style.hiddenCount}>
                          <Chip label={`+${hiddenOptions.size}`} />
                        </div>
                      )}
                    </Chip>
                  </HidableOption>
                );
              })}
            </span>
          )}
        </span>
        {hasSelectedValues && (
          <span
            role="button"
            onClick={handleClear}
            className={style.cancelBtn}
            tabIndex={-1}
          >
            <MaterialIcon
              className="text-base"
              {...getDataAutoTestAttributes(['button-close'])}
            >
              cancel
            </MaterialIcon>
          </span>
        )}
        <span className={style.icon}>
          <MaterialIcon className="text-base">expand_more</MaterialIcon>
        </span>
      </button>
    </div>
  );
}
